import PageWrapper from "@/components/layout/page-wrapper";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { AlignLeft, ChevronDown, Download, Menu, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useSearchParams } from "react-router";
import { useGetAllBillings } from "@/api/billing/get-all-billings";
import { useRef, useEffect, useCallback, useState } from "react";
import PaginationCombo from "@/components/ui/pagination-combo";

const BillingTableDropdown = ({ billid }: { billid: string }) => {
    const navigate = useNavigate();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button size={"sm"} variant="ghost" className="p-2 cursor-pointer">
                    <Menu />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(billid)}>
                    <AlignLeft /> Bill Details
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <Download /> Downlaod Invoice
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const BillingManage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Extract parameters from URL
    const skip = (parseInt(searchParams.get("page") || "1") - 1) * parseInt(searchParams.get("per_page") || "10");
    const limit = parseInt(searchParams.get("per_page") || "10");
    const search = searchParams.get("search") || null;
    const statusParam = searchParams.get("status");
    const status = statusParam && statusParam !== "all" ? statusParam : undefined;
    const startDate = searchParams.get("start_date") || undefined;
    const endDate = searchParams.get("end_date") || undefined;

    // This hook will refetch when any of these parameters change
    const { data: billingData, isLoading } = useGetAllBillings({
        skip,
        limit,
        search,
        status,
        start_date: startDate,
        end_date: endDate,
    });

    const formRef = useRef<HTMLFormElement>(null);
    const statusRef = useRef<HTMLSelectElement>(null);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");

    useEffect(() => {
        if (searchParams.get("search") && formRef.current) {
            (formRef.current).search.value = searchParams.get("search") || "";
        }

        // Set the initial status dropdown value based on URL parameters
        if (statusRef.current) {
            const currentStatus = searchParams.get("status");
            if (currentStatus === "true" || currentStatus === "false") {
                statusRef.current.value = currentStatus;
            } else {
                statusRef.current.value = "all";
            }
        }

        // Set the initial date input values based on URL parameters
        const urlStartDate = searchParams.get("start_date") || "";
        const urlEndDate = searchParams.get("end_date") || "";

        setSelectedStartDate(urlStartDate);
        setSelectedEndDate(urlEndDate);
    }, [searchParams]);

    const handleDateFilter = useCallback(() => {
        const searchValue = formRef.current?.search.value.trim() || "";
        const statusValue = statusRef.current?.value || "all";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.status = statusValue;
        }

        if (selectedStartDate) {
            params.start_date = selectedStartDate; // Value from state is already in YYYY-MM-DD format
        }

        if (selectedEndDate) {
            params.end_date = selectedEndDate; // Value from state is already in YYYY-MM-DD format
        }

        setSearchParams(params);
    }, [formRef, statusRef, selectedStartDate, selectedEndDate, setSearchParams]);

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const searchValue = form.search.value.trim();
        const statusValue = statusRef.current?.value || "all";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.status = statusValue;
        }

        setSearchParams(params);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        const statusValue = target.value;
        const searchValue = formRef.current?.search.value.trim() || "";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.status = statusValue;
        }

        setSearchParams(params);
    };

    const clearFilters = () => {
        setSearchParams({ page: "1", per_page: "10" });
        if (formRef.current) {
            formRef.current.search.value = "";
        }
        if (statusRef.current) {
            statusRef.current.value = "all";
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <PageWrapper pageTitle="Billing" currentPage="Billing">
            <Card className="w-full mx-auto p-4">
                <div className="flex justify-end items-center mb-4">
                    <div className="flex gap-2 items-center flex-wrap">
                        <form
                            ref={formRef}
                            className="flex border rounded-md"
                            onSubmit={handleSearchSubmit}
                        >
                            <Input placeholder="Search billing..." className="border-none" name="search" />
                            <Button variant={"ghost"} type="submit">
                                <Search />
                            </Button>
                        </form>

                        <div className="relative">
                            <select
                                ref={statusRef}
                                onChange={handleStatusChange}
                                className="w-full rounded-md border border-input bg-background py-2 pl-3 pr-8 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_[data-icon]]:size-4 appearance-none"
                            >
                                <option value="all">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none" />
                        </div>

                        <div className="flex gap-2">
                            <Input
                                type="date"
                                placeholder="Start date"
                                className="w-36"
                                value={selectedStartDate}
                                onChange={(e) => setSelectedStartDate(e.target.value)}
                            />
                            <Input
                                type="date"
                                placeholder="End date"
                                className="w-36"
                                value={selectedEndDate}
                                min={selectedStartDate || undefined}
                                onChange={(e) => setSelectedEndDate(e.target.value)}
                            />
                            <Button type="button" onClick={handleDateFilter}>
                                Filter
                            </Button>
                        </div>

                        {(searchParams.get("search") || searchParams.get("status") || searchParams.get("start_date") || searchParams.get("end_date")) && (
                            <Button onClick={clearFilters}>
                                Clear
                            </Button>
                        )}
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <Checkbox />
                            </TableHead>
                            <TableHead>Store Name</TableHead>
                            <TableHead>Customer Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Start Date</TableHead>
                            <TableHead>End Date</TableHead>
                            <TableHead>Plan Name</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead className="flex justify-end">Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {billingData?.data?.map((invoice: any) => (
                            <TableRow key={invoice.id} className="hover:bg-muted/20">
                                <TableCell>
                                    <Checkbox />
                                </TableCell>
                                <TableCell className="text-blue-600 font-medium">
                                    <a href={`store-management/store/${invoice?.store_id}`}>{invoice?.store_name}</a>
                                </TableCell>
                                <TableCell>{invoice?.store_owner_name}</TableCell>
                                <TableCell>
                                    <Badge className={invoice.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                        {invoice.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell>{new Date(invoice?.start_date).toDateString()}</TableCell>
                                <TableCell>{new Date(invoice?.end_date).toDateString()}</TableCell>
                                <TableCell className="font-medium">{invoice?.plan_name}</TableCell>
                                <TableCell className="font-medium">${invoice.plan_price}</TableCell>
                                <TableCell className="flex justify-end">
                                    <div className="flex items-center gap-2">
                                        <BillingTableDropdown billid={invoice.id} />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <div className="mt-4">
                    <PaginationCombo totalItems={billingData?.pagination?.total_entity} />
                </div>
            </Card>
        </PageWrapper>
    );
};

export default BillingManage;
